import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  useMediaQuery,
  useTheme,
} from "components/mui";
import { useNavigate } from 'react-router-dom';
import { getListContacts } from 'api/canmail/CANmailAPI';
import { ROUTE_CONTACTS } from 'constants/routerPath';

interface Contact {
  contactId: number | string;
  contactName: string;
  emailAddress: string;
}

interface RecipientsListProps {
  listId: string | number | null;
}

/**
 * @component RecipientsList
 * @description Displays a list of contacts from a mailing list in a table format
 * @param {Object} props
 * @param {string|number|null} props.listId - The mailing list ID to fetch contacts from
 * @returns {JSX.Element|null}
 */
export function RecipientsList({ listId }: RecipientsListProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // The full contact list for the selected mailing list. `getListContacts` has no
  // server-side pagination, so this is fetched once per list and paged client-side.
  // The effect previously also depended on `page`, which meant every page change
  // re-downloaded the entire list.
  const [allContacts, setAllContacts] = useState<Contact[]>([]);
  const [page, setPage] = useState(0);
  const pageSize = 15; // Fixed page size
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadContacts = async () => {
      if (!listId) {
        setAllContacts([]);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const LIST_TYPE_ID = 1; // Standard mailing list type
        const contacts = await getListContacts(listId, LIST_TYPE_ID);
        if (cancelled) return;

        if (!Array.isArray(contacts)) {
          setAllContacts([]);
          return;
        }

        // Transform contacts to match recipient format
        setAllContacts(
          contacts.map((contact: any) => {
            const contactId = contact.id || contact.contactID || contact.ContactID;
            const firstName = contact.first_name || contact.firstName || '';
            const lastName = contact.last_name || contact.lastName || '';
            const email = contact.email_address || contact.email || contact.Email || '';

            // Build contact name
            let contactName = '';
            if (firstName && lastName) {
              contactName = `${firstName} ${lastName}`.trim();
            } else if (firstName) {
              contactName = firstName;
            } else if (lastName) {
              contactName = lastName;
            } else if (contact.name) {
              contactName = contact.name;
            } else {
              contactName = 'Unknown';
            }

            return { contactId, contactName, emailAddress: email };
          })
        );
      } catch (err: any) {
        if (cancelled) return;
        console.error('[RecipientsList] Failed to fetch contacts:', err);
        setError(err.message || 'Failed to load contacts');
        setAllContacts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadContacts();

    // Switching lists quickly must not let a slow earlier response overwrite a newer one.
    return () => {
      cancelled = true;
    };
  }, [listId]);

  // Switching lists must reset paging — otherwise landing on page 3 of a long list and
  // then picking a short one shows an empty table.
  useEffect(() => {
    setPage(0);
  }, [listId]);

  const totalContacts = allContacts.length;

  const contacts = useMemo(
    () => allContacts.slice(page * pageSize, page * pageSize + pageSize),
    [allContacts, page]
  );

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  if (!listId) {
    return null;
  }

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
      }}
    >
      <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
        Recipients ({totalContacts})
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isMobile ? (
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : contacts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No contacts found
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2} sx={{ mb: 2 }}>
              {contacts.map((contact) => (
                <Card
                  key={contact.contactId}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: 3,
                    },
                  }}
                  onClick={() => {
                    navigate(`${ROUTE_CONTACTS}?contactId=${contact.contactId}`);
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                      {contact.contactName || 'Unknown'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {contact.emailAddress}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
          <TablePagination
            component="div"
            count={totalContacts}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[]}
            labelRowsPerPage=""
          />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : contacts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No contacts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  contacts.map((contact) => (
                    <TableRow
                      key={contact.contactId}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        navigate(`${ROUTE_CONTACTS}?contactId=${contact.contactId}`);
                      }}
                    >
                      <TableCell>{contact.contactName || 'Unknown'}</TableCell>
                      <TableCell>{contact.emailAddress}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalContacts}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[]}
            labelRowsPerPage=""
          />
        </Box>
      )}
    </Paper>
  );
}
